/**
 * Transition Constants - Sprint 20+
 * Default durations, validation rules, and configuration for video transitions
 */

import { TransitionType, SceneTransition } from '../../../types/scene';

/**
 * Default durations for each transition type (in seconds)
 * Based on professional video editing standards
 */
export const DEFAULT_TRANSITION_DURATIONS: Record<TransitionType, number> = {
  cut: 0,        // Instant cut (no transition)
  fade: 1.2,     // Classic fade to black timing
  crossfade: 1.5, // Smooth dissolve
  slide: 1.5,    // Comfortable slide speed
};

/**
 * Minimum allowed duration for each transition type (in seconds)
 */
export const MIN_TRANSITION_DURATIONS: Record<TransitionType, number> = {
  cut: 0,       // Cuts are instant
  fade: 0.3,    // Too fast looks jarring
  crossfade: 0.5, // Needs time to dissolve smoothly
  slide: 0.5,   // Needs time to slide smoothly
};

/**
 * Maximum allowed duration for each transition type (in seconds)
 */
export const MAX_TRANSITION_DURATIONS: Record<TransitionType, number> = {
  cut: 0,       // Cuts are instant
  fade: 3.0,    // Longer than 3s feels slow
  crossfade: 5.0, // Can be longer for artistic effect
  slide: 4.0,   // Longer than 4s feels sluggish
};

/**
 * Get the default transition for a specific type
 * DEFECT FIX #5: Make type parameter optional with safe default
 */
export function getDefaultTransition(type: TransitionType = 'cut'): SceneTransition {
  return {
    type,
    duration: DEFAULT_TRANSITION_DURATIONS[type] ?? 0,
    easing: 'ease-in-out',
  };
}

/**
 * Validate and clamp transition duration to allowed range
 */
export function validateTransitionDuration(type: TransitionType, duration: number): number {
  const min = MIN_TRANSITION_DURATIONS[type];
  const max = MAX_TRANSITION_DURATIONS[type];
  
  if (type === 'cut') {
    return 0; // Cut is always instant
  }
  
  return Math.max(min, Math.min(max, duration));
}

/**
 * Check if a duration is valid for a given transition type
 */
export function isValidTransitionDuration(type: TransitionType, duration: number): boolean {
  if (type === 'cut') {
    return duration === 0;
  }
  
  const min = MIN_TRANSITION_DURATIONS[type];
  const max = MAX_TRANSITION_DURATIONS[type];
  
  return duration >= min && duration <= max;
}

/**
 * Get duration range label for UI display
 */
export function getTransitionDurationRange(type: TransitionType): string {
  if (type === 'cut') {
    return 'Instant';
  }
  
  const min = MIN_TRANSITION_DURATIONS[type];
  const max = MAX_TRANSITION_DURATIONS[type];
  
  return `${min}s - ${max}s`;
}

/**
 * Get recommended duration for preview (used in TransitionPreviewModal)
 * For cut transitions, we show a brief preview animation
 */
export function getPreviewDuration(transition: SceneTransition): number {
  if (transition.type === 'cut') {
    // Show a brief 0.3s animation for cut preview so users can see the transition
    return 0.3;
  }
  
  return transition.duration;
}
